import * as React from "react";
import {
  AmbientBackground,
  Navbar,
  Hero,
  Stats,
  Features,
  HowItWorks,
  Benefits,
  Testimonials,
  Pricing,
  FAQ,
  FinalCTA,
  Footer,
  AppScreenshots,
  SectionLabel,
} from "@/components/landing";
import { Check } from "lucide-react";
import { QRCode } from "react-qr-code";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import seedData from "../../../seed-data.json";

// Type for section order
type SectionKey = (typeof seedData.sectionOrder)[number];

// Section component map
const sectionComponents: Record<SectionKey, React.ReactNode> = {
  stats: <Stats stats={seedData.stats} />,
  features: (
    <Features
      features={seedData.features}
      heading={seedData.productDetail.heading}
      description={seedData.productDetail.description}
    />
  ),
  howItWorks: seedData.howItWorks && (
    <HowItWorks
      title={seedData.howItWorks.title}
      subtitle={seedData.howItWorks.subtitle}
      steps={seedData.howItWorks.steps}
    />
  ),
  benefits: seedData.benefits && (
    <Benefits
      title={seedData.benefits.title}
      subtitle={seedData.benefits.subtitle}
      items={seedData.benefits.items}
    />
  ),
  testimonials: seedData.testimonials && (
    <Testimonials
      title={seedData.testimonials.title}
      subtitle={seedData.testimonials.subtitle}
      testimonials={seedData.testimonials.items}
    />
  ),
  pricing: (seedData as any).pricing && (
    <Pricing
      title={(seedData as any).pricing.title}
      subtitle={(seedData as any).pricing.subtitle}
      billingNote={(seedData as any).pricing.billingNote}
      tiers={(seedData as any).pricing.tiers}
    />
  ),
  faq: seedData.faq && (
    <FAQ
      title={seedData.faq.title}
      subtitle="Everything you need to know about Board Game Genie"
      items={seedData.faq.mainEntity}
    />
  ),
  finalCTA: null, // rendered inline to pass onClick handler
  // Mobile app sections
  appScreenshots: (seedData as any).appScreenshots && (
    <AppScreenshots
      title={(seedData as any).appScreenshots.title}
      subtitle={(seedData as any).appScreenshots.subtitle}
      items={(seedData as any).appScreenshots.items}
    />
  ),
  featureSpotlight: (seedData as any).featureSpotlight && (
    <section className="py-24 lg:py-32 bg-[#FFFBF0]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left side - Content */}
          <div>
            <SectionLabel color="blue" className="mb-6">
              Trust
            </SectionLabel>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4 font-['Epilogue'] text-[#1A1A1A]">
              {(seedData as any).featureSpotlight.title}
            </h2>

            <p className="text-lg lg:text-xl text-[#666666] mb-4 font-['Sora'] font-medium">
              {(seedData as any).featureSpotlight.subtitle}
            </p>

            <p className="text-[#666666] mb-8 font-['Sora'] leading-relaxed">
              {(seedData as any).featureSpotlight.description}
            </p>

            {/* Feature list with neo-brutalist cards */}
            <ul className="space-y-4">
              {(seedData as any).featureSpotlight.features.map(
                (feature: any, index: number) => (
                  <li
                    key={index}
                    className="group flex gap-4 p-4 bg-white rounded-xl border-3 border-black shadow-[4px_4px_0_0_#000000] transition-all duration-200 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#3F5EF4]"
                  >
                    {/* Checkmark icon box */}
                    <div className="w-10 h-10 rounded-lg bg-[#3F5EF4] border-2 border-black shadow-[2px_2px_0_0_#000000] flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-white" strokeWidth={3} />
                    </div>
                    <div>
                      <p className="font-bold text-[#1A1A1A] font-['Epilogue']">
                        {feature.title}
                      </p>
                      <p className="text-sm text-[#666666] font-['Sora']">
                        {feature.description}
                      </p>
                    </div>
                  </li>
                ),
              )}
            </ul>
          </div>

          {/* Right side - Feature image placeholder */}
          <div className="bg-white rounded-2xl border-3 border-black shadow-[8px_8px_0_0_#000000] aspect-square flex items-center justify-center text-[#666666] font-['Sora']">
            [Feature Image]
          </div>
        </div>
      </div>
    </section>
  ),
  appDemoVideo: (seedData as any).appDemoVideo && (
    <section className="py-24 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl font-bold mb-4">
          {(seedData as any).appDemoVideo.title}
        </h2>
        <p className="text-muted-foreground mb-8">
          {(seedData as any).appDemoVideo.description}
        </p>
        <div className="relative aspect-video rounded-2xl overflow-hidden bg-black">
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${(seedData as any).appDemoVideo.videoId}`}
            title="Demo Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  ),
  appStoreDownload: (seedData as any).appStoreDownload && (
    <section className="py-24 px-6 bg-muted/30">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl font-bold mb-4">
          {(seedData as any).appStoreDownload.headline}
        </h2>
        <p className="text-muted-foreground mb-8">
          {(seedData as any).appStoreDownload.description}
        </p>
        <div className="flex justify-center mb-8">
          <div className="w-80 h-80 max-w-full bg-white rounded-xl border-3 border-black shadow-[4px_4px_0_0_#000000] flex flex-col items-center justify-center gap-4 p-6">
            <QRCode
              value={(seedData as any).appStoreDownload.qrCodeUrl}
              size={256}
              style={{ width: "100%", height: "auto", maxWidth: 256 }}
              level="H"
              bgColor="#ffffff"
              fgColor="#000000"
            />
            <span className="text-2xl font-medium text-[#666666] font-['Sora']">
              Scan to download
            </span>
          </div>
        </div>
        <div className="flex gap-4 justify-center flex-wrap">
          <a
            href={(seedData as any).appStoreDownload.appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-xl hover:bg-black/80 transition-colors"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <div className="text-left">
              <div className="text-xs">Download on the</div>
              <div className="text-sm font-semibold">App Store</div>
            </div>
          </a>
        </div>
      </div>
    </section>
  ),
};

export function LandingPage() {
  const [showDownloadModal, setShowDownloadModal] = React.useState(false);

  const handleDownloadClick = () => setShowDownloadModal(true);

  return (
    <div className="relative min-h-screen">
      <AmbientBackground />

      <Navbar
        companyName={seedData.organization.name}
        ctaText={seedData.hero.primaryCTA.text}
        onCTAClick={handleDownloadClick}
      />

      <main>
        <Hero
          headline={seedData.hero.headline}
          subheadline={seedData.hero.subheadline}
          primaryCTA={seedData.hero.primaryCTA}
          onPrimaryCTAClick={handleDownloadClick}
          trustIndicator={seedData.hero.trustIndicator}
        />

        {/* Render sections based on sectionOrder */}
        {seedData.sectionOrder.map((sectionKey) => (
          <React.Fragment key={sectionKey}>
            {sectionComponents[sectionKey]}
          </React.Fragment>
        ))}

        {/* FinalCTA rendered inline to pass onClick handler */}
        {seedData.finalCTA && (
          <FinalCTA
            headline={seedData.finalCTA.headline}
            description={seedData.finalCTA.description}
            primaryCTA={seedData.finalCTA.primaryCTA}
            onPrimaryCTAClick={handleDownloadClick}
          />
        )}
      </main>

      <Footer
        company={seedData.footer.company}
        navigation={seedData.footer.navigation}
        social={seedData.footer.social}
      />

      {/* App Store Download Modal */}
      <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
        <DialogContent
          className="sm:max-w-md"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-center font-['Epilogue'] text-xl font-black">
              {(seedData as any).appStoreDownload.headline}
            </DialogTitle>
            <DialogDescription className="text-center font-['Sora']">
              {(seedData as any).appStoreDownload.description}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4 pb-2">
            <div className="w-80 h-80 max-w-full bg-white rounded-xl border-3 border-black shadow-[4px_4px_0_0_#000000] flex flex-col items-center justify-center gap-4 p-6">
              <QRCode
                value={(seedData as any).appStoreDownload.qrCodeUrl}
                size={192}
                style={{ width: "100%", height: "auto", maxWidth: 192 }}
                level="H"
                bgColor="#ffffff"
                fgColor="#000000"
              />
              <span className="text-2xl font-medium text-[#666666] font-['Sora']">
                Scan to download
              </span>
            </div>
          </div>
          <div className="flex gap-4 justify-center flex-wrap pt-4">
            <a
              href={(seedData as any).appStoreDownload.appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-xl hover:bg-black/80 transition-colors"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <div className="text-left">
                <div className="text-xs">Download on the</div>
                <div className="text-sm font-semibold">App Store</div>
              </div>
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LandingPage;
